import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Shield, Mail, Lock, Loader2, CheckCircle } from 'lucide-react';
import { trackSubscribe } from '../utils/metaPixel';
import SEO from '../components/SEO';
import { API_URL } from '../config';
import '../styles/Auth.css';


// Brand logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_express-checkout-9/artifacts/65fw8myi_Variant%201_Full%20Logo_Vector%20copy.png";

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const navigate = useNavigate();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/signup`, { email, password });
      
      // Track Subscribe event on successful signup
      trackSubscribe({ content_name: 'Account Signup' });
      
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Success state - show verification message
  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <Link to="/" className="auth-logo">
              <img src={LOGO_URL} alt="All Secured Logo" />
            </Link>
          </div>
          
          <div className="auth-status-container success" style={{ marginTop: '24px' }}>
            <CheckCircle size={64} className="success-icon" />
            <h2>Check Your Email</h2>
            <p style={{ marginBottom: '16px' }}>
              We've sent a verification link to <strong>{email}</strong>
            </p>
            <p className="auth-note">
              Please check your inbox and click the link to verify your account.
            </p>
            <div className="auth-actions" style={{ marginTop: '24px' }}>
              <Link to="/login" className="auth-secondary-btn">
                Go to Login
              </Link>
            </div>
            <p className="auth-note" style={{ marginTop: '24px', fontSize: '13px' }}>
              Didn't receive the email?{' '}
              <Link to="/resend-verification" className="auth-link">
                Resend verification
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="auth-page">
      <SEO canonical="/signup" noIndex={true} />
      <div className="auth-container">
        <div className="auth-header">
          <Link to="/" className="auth-logo">
            <img src={LOGO_URL} alt="All Secured Logo" />
          </Link>
          <h1>Join All Secured</h1>
          <p>Create an account to start learning</p>
        </div>
        
        <form className="auth-form" onSubmit={handleSubmit}>
          {error && (
            <div className="auth-error">
              <Shield size={18} />
              <span>{error}</span>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="email">
              <Mail size={18} />
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              disabled={isLoading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">
              <Lock size={18} />
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
              required
              disabled={isLoading}
              minLength={8}
            />
            <span className="form-hint">At least 8 characters</span>
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">
              <Lock size={18} />
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
              disabled={isLoading}
              minLength={8}
            />
          </div>
          
          <button type="submit" className="auth-button" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 size={20} className="spinner" />
                Creating account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>
        
        <div className="auth-footer">
          <p>Already have an account?</p>
          <Link to="/login" className="auth-link">Sign In</Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;
